import { Children, Fragment, isValidElement, useEffect, useState, type ReactElement, type ReactNode } from 'react'
import { atom, useAtomValue } from 'jotai'
import { EDGE_INSET } from '@/constants/insets'
import { Band, bandChannelWidth, bandPillWidth, type BandItem, type BandMetrics, type BandRegion } from './Band'
import { currentPageAtom } from './pageRegistry'
import { barMarkColor, SLIDING_LOOKS, useSelectedLook } from './selectedLook'

/*
 * A page's top bar on the band (owner, 2026-10-15: "we should also reuse the main top palette /
 * tray for pages. for now we can keep their current icons/buttons, but they need to go into the
 * tray that can cycle them around / fits into the screen"). `WorldBar edge="top"` renders this:
 * the page's own buttons (WorldButtons, unchanged: icons, domes, shortcuts and hints) ride the
 * kit's band (src/play/Band.tsx), placed as the editor's palette is:
 *
 *   [home] ╰( 1 2 3 4 5 E S )╯ [settings]
 *                              [undo]
 *                              [clear]
 *
 * - Along the top edge between home and the corner column, never under home. When the buttons do
 *   not all fit, the ends bend up into channels and the row scrolls (drag, wheel, trackpad), stopping
 *   at its first and last button; a shortcut or a tap brings the picked button onto the straight run.
 * - No growing: page buttons are already full size (72 px kid targets), so the band rests at that
 *   size and a finger's target never changes size under it. No scrim either: picking a cup and
 *   pouring is one quick move, and the scene stays live beside the bar as it did.
 * - On a screen too narrow for three buttons between home and the corner column (a phone upright),
 *   as the editor's palette, it lies along the bottom edge instead, full width, scaled down only as
 *   far as needed to show three buttons on its straight run (`usePageBarLayout` lifts the left
 *   column and the bottom-left action above it).
 * - Selected: the bar's sliding backing (the selected look), riding with the selected button.
 * - Top or bottom: an owner setting (`pageBarEdgeAtom`, owner, 2026-10-15: "lets have a setting for
 *   whether to put the tool palette on top or bottom? i am not sure what i like"). At the bottom it
 *   lies as on a phone upright, on every screen: full width, the left column and the bottom-left
 *   action lifted above it. The band and its buttons are unchanged; only the edge.
 */

/** The edge the page's main bar lies along when it has the room (`auto`). */
export type PageBarEdge = 'top' | 'bottom'

const STORAGE_KEY = 'huebo:pageBarEdge'

function storedPageBarEdge(): PageBarEdge {
  try {
    return localStorage.getItem(STORAGE_KEY) === 'bottom' ? 'bottom' : 'top'
  } catch {
    return 'top'
  }
}

/** Read outside React too (a world sized round the band, `pageBandPlacement`); kept with the atom. */
let pageBarEdge: PageBarEdge = storedPageBarEdge()
const pageBarEdgeBaseAtom = atom<PageBarEdge>(pageBarEdge)
/** Where every page's main bar lies: top (default) or bottom. On every page; remembered on this device. */
export const pageBarEdgeAtom = atom(
  (get) => get(pageBarEdgeBaseAtom),
  (_get, set, edge: PageBarEdge) => {
    pageBarEdge = edge
    set(pageBarEdgeBaseAtom, edge)
    try { localStorage.setItem(STORAGE_KEY, edge) } catch { /* private mode */ }
  },
)

/** The corner buttons' size and what the band must clear on each side (home; settings and its column). */
const CORNER = 60
const GAP = 14
const CORNER_CLEAR = EDGE_INSET + CORNER + GAP
/** The page band's bend (design px): the editor's look at the page buttons' scale. */
const BEND = 72

/** The band's metrics for buttons `size` px across (WorldBar's 14 px gap and 12 px padding). */
export function pageBandMetrics(size = 72, k = 1): BandMetrics {
  const tray = size + 24
  return { item: size, pitch: size + GAP, tray, rest: tray * k, bend: BEND }
}

export type PageBandPlacement = { region: BandRegion; k: number; metrics: BandMetrics; height: number }

/**
 * Where a page band lies: `auto` (the main bar's place: along the top between home and the corner
 * column, or along the bottom on a screen too narrow for that or when the owner put it there,
 * `pageBarEdgeAtom`) or `bottom` (always along the bottom edge: a category
 * tray's options, src/play/CategoryTray.tsx).
 */
export type PageBandAt = 'auto' | 'bottom'

/**
 * Where the band for `n` buttons of `size` px goes on a screen `w` px wide, and its scale. `edge`
 * is the owner's setting (a component passes the atom's value, so it follows a change).
 */
export function pageBandPlacement(w: number, n: number, size = 72, at: PageBandAt = 'auto', edge: PageBarEdge = pageBarEdge): PageBandPlacement {
  const full = pageBandMetrics(size)
  const pill = bandPillWidth(full, n)
  const three = 3 * full.pitch + 2 * bandChannelWidth(full)
  const topRoom = w - 2 * CORNER_CLEAR
  let region: BandRegion
  let k = 1
  if (at === 'auto' && edge === 'top' && (pill <= topRoom || three <= topRoom)) region = { left: CORNER_CLEAR, right: w - CORNER_CLEAR, edge: 'top' }
  else {
    region = { left: EDGE_INSET, right: w - EDGE_INSET, edge: 'bottom' }
    const room = region.right - region.left
    // As large as shows them all as a pill, or else three on the straight run.
    k = Math.min(1, (room - 1) / Math.min(pill, three))
  }
  const metrics = pageBandMetrics(size, k)
  return { region, k, metrics, height: metrics.rest }
}

/** The viewport's size, current across resizes. */
export function useViewport(): { w: number; h: number } {
  const read = () => ({ w: window.innerWidth, h: window.innerHeight })
  const [size, setSize] = useState(read)
  useEffect(() => {
    const on = () => setSize((prev) => {
      const next = read()
      return prev.w === next.w && prev.h === next.h ? prev : next
    })
    window.addEventListener('resize', on)
    return () => window.removeEventListener('resize', on)
  }, [])
  return size
}

/** The bar's children as a flat list (arrays and fragments opened; nothing rendered for null). */
function flatten(children: ReactNode): ReactElement[] {
  const out: ReactElement[] = []
  Children.forEach(children, (c) => {
    if (!isValidElement(c)) return
    if (c.type === Fragment) out.push(...flatten((c.props as { children?: ReactNode }).children))
    else out.push(c)
  })
  return out
}

/** A page's top bar as a band: each child (a WorldButton) one item. */
export function PageBand({ children, background = 'rgba(255,255,255,0.72)', accent, at = 'auto' }: {
  children: ReactNode
  background?: string
  accent?: string
  /** Where it lies (`PageBandAt`): the top bar's place by default. */
  at?: PageBandAt
}) {
  const { w } = useViewport()
  const edge = useAtomValue(pageBarEdgeAtom)
  const look = useSelectedLook()
  const pageAccent = useAtomValue(currentPageAtom)?.accent
  const kids = flatten(children)
  const size = Math.max(0, ...kids.map((c) => (c.props as { size?: number }).size ?? 72)) || 72
  const { region, metrics } = pageBandPlacement(w, kids.length, size, at, edge)
  const items: BandItem[] = kids.map((c, i) => ({ key: c.key ?? i, node: c }))
  return (
    <Band
      items={items}
      metrics={metrics}
      region={region}
      grow={false}
      scrim={false}
      background={background}
      mark={SLIDING_LOOKS.has(look) ? 'backing' : 'none'}
      markColor={barMarkColor(accent ?? pageAccent, background, 0.42)}
      hoverKey="page-band"
      rootProps={{ 'data-page-band': '' }}
    />
  )
}
